import * as React from 'react';
import { useQuery, UseQueryResult } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { HttpClient } from 'apiclient/ApiClient';
import { groupReportsByTime, GroupedReports, ReportSummary } from 'utils/reportUtils';

export interface UseReportsListConfig {
    apiEndpoint: string;
    queryKey: string;
    enabled?: boolean;
    reportUrlPrefix: string; // e.g., '/teacher_stat/' or '/organization_stat/'
}

export interface UseReportsListReturn<T extends ReportSummary> {
    // Data
    reports: T[];
    groupedReports: GroupedReports<T>;
    
    // State
    isLoading: boolean;
    error: any;
    
    // Actions
    refetch: () => Promise<any>;
    navigateToReport: (reportId: string) => void;
}

export function useReportsList<T extends ReportSummary>(
    config: UseReportsListConfig
): UseReportsListReturn<T> {
    const httpClient = HttpClient();
    const navigate = useNavigate();
    
    const {
        apiEndpoint,
        queryKey,
        enabled = true,
        reportUrlPrefix
    } = config;

    // Fetch reports
    const reportsQuery: UseQueryResult<T[]> = useQuery(
        queryKey,
        async () => await httpClient.get(apiEndpoint),
        { enabled }
    );

    // Memoize reports to prevent unnecessary re-renders
    const reports = React.useMemo(() => {
        return reportsQuery.data || [];
    }, [reportsQuery.data]);

    // Group reports by time
    const groupedReports = React.useMemo(() => {
        return groupReportsByTime(reports);
    }, [reports]);

    // Navigation handler
    const navigateToReport = React.useCallback((reportId: string) => {
        navigate(`${reportUrlPrefix}${reportId}`);
    }, [navigate, reportUrlPrefix]);

    return {
        // Data
        reports,
        groupedReports,
        
        // State
        isLoading: reportsQuery.isLoading,
        error: reportsQuery.error,
        
        // Actions
        refetch: reportsQuery.refetch,
        navigateToReport
    };
}